/**
 * Filters of the back-office events list. The whole state lives in the page URL
 * (shareable link, back button restores the previous filters), so this module
 * turns route params <-> a clean filter object <-> the API query.
 */

export type AdminEventStatus = 'upcoming' | 'ongoing' | 'ended'
export type AdminEventPricing = 'free' | 'paid'
export type AdminEventSort = 'date' | 'attendees' | 'paid'
export type AdminEventOrder = 'asc' | 'desc'

export const ADMIN_EVENT_STATUSES: AdminEventStatus[] = ['upcoming', 'ongoing', 'ended']
export const ADMIN_EVENT_PRICINGS: AdminEventPricing[] = ['free', 'paid']
export const ADMIN_EVENT_CATEGORIES = [
  'networking',
  'conference',
  'workshop',
  'meetup',
  'trade_show',
  'seminar',
  'other',
] as const
export const ADMIN_EVENT_FORMATS = ['in_person', 'online', 'hybrid'] as const
export const ADMIN_EVENT_SORTS: AdminEventSort[] = ['date', 'attendees', 'paid']

export interface AdminEventsFilters {
  search: string
  status: AdminEventStatus | ''
  pricing: AdminEventPricing | ''
  category: string
  format: string
  organizerId: string
  /** YYYY-MM-DD, Paris days. */
  from: string
  to: string
  sort: AdminEventSort | ''
  order: AdminEventOrder | ''
  page: number
}

export const EMPTY_ADMIN_EVENTS_FILTERS: AdminEventsFilters = {
  search: '',
  status: '',
  pricing: '',
  category: '',
  format: '',
  organizerId: '',
  from: '',
  to: '',
  sort: '',
  order: '',
  page: 1,
}

const DAY = /^\d{4}-\d{2}-\d{2}$/
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

const text = (value: unknown): string => (typeof value === 'string' ? value : '')
const oneOf = <T extends string>(value: unknown, allowed: readonly T[]): T | '' =>
  allowed.includes(value as T) ? (value as T) : ''

/** The pre-filter-panel shortcut of the overview screen (`filter=paid`, `filter=past`…). */
const LEGACY: Record<string, Partial<AdminEventsFilters>> = {
  upcoming: { status: 'upcoming' },
  ongoing: { status: 'ongoing' },
  past: { status: 'ended' },
  paid: { pricing: 'paid' },
}

/** Route params (always strings, whatever the user typed in the URL) -> a valid filter object. */
export function parseAdminEventsParams(params: Record<string, unknown> | undefined): AdminEventsFilters {
  const p = params ?? {}
  const page = Number.parseInt(text(p.page), 10)
  const filters: AdminEventsFilters = {
    search: text(p.search).slice(0, 100),
    status: oneOf(p.status, ADMIN_EVENT_STATUSES),
    pricing: oneOf(p.pricing, ADMIN_EVENT_PRICINGS),
    category: oneOf(p.category, ADMIN_EVENT_CATEGORIES),
    format: oneOf(p.format, ADMIN_EVENT_FORMATS),
    organizerId: UUID.test(text(p.organizerId)) ? text(p.organizerId) : '',
    from: DAY.test(text(p.from)) ? text(p.from) : '',
    to: DAY.test(text(p.to)) ? text(p.to) : '',
    sort: oneOf(p.sort, ADMIN_EVENT_SORTS),
    order: oneOf(p.order, ['asc', 'desc'] as const),
    page: Number.isFinite(page) && page > 0 ? page : 1,
  }
  const legacy = LEGACY[text(p.filter)]
  return legacy ? { ...filters, ...legacy } : filters
}

/** Filters -> route params: only what differs from the default, so URLs stay short. */
export function toAdminEventsParams(filters: AdminEventsFilters): Record<string, string | undefined> {
  const out: Record<string, string | undefined> = {}
  for (const key of ['search', 'status', 'pricing', 'category', 'format', 'organizerId', 'from', 'to', 'sort', 'order'] as const) {
    out[key] = filters[key] ? filters[key] : undefined
  }
  out.page = filters.page > 1 ? String(filters.page) : undefined
  return out
}

/** Query string sent to `GET /admin/events` (empty values are left out). */
export function toAdminEventsQuery(filters: AdminEventsFilters, limit: number): Record<string, string | number> {
  const query: Record<string, string | number> = { page: filters.page, limit }
  for (const key of ['search', 'status', 'pricing', 'category', 'format', 'organizerId', 'from', 'to', 'sort', 'order'] as const) {
    const value = key === 'search' ? filters.search.trim() : filters[key]
    if (value) query[key] = value
  }
  return query
}

/** Number of active filters shown on the "Filters" button (the search box is visible on its own). */
export function countActiveAdminEventsFilters(filters: AdminEventsFilters): number {
  let count = 0
  if (filters.status) count += 1
  if (filters.pricing) count += 1
  if (filters.category) count += 1
  if (filters.format) count += 1
  if (filters.organizerId) count += 1
  if (filters.from || filters.to) count += 1
  if (filters.sort) count += 1
  return count
}

export const hasAnyAdminEventsFilter = (filters: AdminEventsFilters): boolean =>
  countActiveAdminEventsFilters(filters) > 0 || filters.search.trim() !== ''

/* ---------- Period shortcuts: whole days in Europe/Paris ---------- */

const PARIS_DAY = new Intl.DateTimeFormat('en-CA', {
  timeZone: 'Europe/Paris',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
})

/** The calendar day (YYYY-MM-DD) it currently is in Paris, whatever the device time zone. */
export function parisDay(now: Date = new Date()): string {
  return PARIS_DAY.format(now)
}

const toUtc = (day: string) => new Date(`${day}T00:00:00Z`)
const fromUtc = (date: Date) => date.toISOString().slice(0, 10)
const addDays = (day: string, days: number) => {
  const date = toUtc(day)
  date.setUTCDate(date.getUTCDate() + days)
  return fromUtc(date)
}

export type PeriodPreset = 'today' | 'week' | 'month' | 'last30'

/**
 * today: today · week: Monday to Sunday of this week · month: 1st to last day of
 * this month · last30: the 30 days ending today (today included).
 */
export function periodPreset(preset: PeriodPreset, now: Date = new Date()): { from: string; to: string } {
  const today = parisDay(now)
  switch (preset) {
    case 'today':
      return { from: today, to: today }
    case 'week': {
      const weekday = toUtc(today).getUTCDay() // 0 = Sunday
      const monday = addDays(today, -((weekday + 6) % 7))
      return { from: monday, to: addDays(monday, 6) }
    }
    case 'month': {
      const [year, month] = today.split('-').map(Number)
      const last = new Date(Date.UTC(year, month, 0)).getUTCDate()
      return { from: `${today.slice(0, 8)}01`, to: `${today.slice(0, 8)}${String(last).padStart(2, '0')}` }
    }
    case 'last30':
      return { from: addDays(today, -29), to: today }
  }
}

/** Which shortcut (if any) the current period corresponds to, to highlight it. */
export function activePreset(from: string, to: string, now: Date = new Date()): PeriodPreset | null {
  for (const preset of ['today', 'week', 'month', 'last30'] as const) {
    const range = periodPreset(preset, now)
    if (range.from === from && range.to === to) return preset
  }
  return null
}

/** from > to would be refused by the API (400): catch it in the form first. */
export const isInvalidPeriod = (from: string, to: string): boolean => !!from && !!to && from > to

/** "YYYY-MM-DD" of a device-local Date (what the native date wheel returns). */
export function toDayValue(date: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`
}
